/**
 * PERSON API — Wrapper dei REST Liferay allineato a PeopleResourceImpl.java.
 * I 5 metodi CRUD standard vengono da `createCrudApi`; sopra si aggiunge la
 * lookup anti-duplicato `getByEmailAddress`.
 *
 * Contratto backend (rest-openapi.yaml):
 *   - GET    /people/{groupId}   (+ page, pageSize, sort, search, kanbanId)  -> Page<Person>
 *   - POST   /people/{groupId}  PersonInput                                  -> Person
 *   - GET    /people/{groupId}/by-email-address?emailAddress=…  duplicato -> Person | 204
 *   - GET    /person/{personId}                                              -> Person
 *   - PATCH  /person/{personId} PersonInput (parziale)                       -> Person
 *   - DELETE /person/{personId}                                              -> 200
 *
 * Plurale backend "people" (irregolare), singolo "person". Sub-resources
 * (emailAddress, phone) in file dedicati. `personUpdateToInput` ignora `personId`
 * (whitelist).
 */

import { httpClient } from '../../api/httpClient';
import { createCrudApi } from '../../store/createCrudApi';
import {
  personFromDto,
  personCreateToInput,
  personUpdateToInput,
  type PersonDto,
  type Person,
} from './personMappers';
import type { PersonCreatePayload, PersonListParams, PersonUpdatePayload } from './personTypes';

const COLLECTION = '/people';

const base = createCrudApi<Person, PersonDto, PersonCreatePayload, PersonUpdatePayload, PersonListParams>({
  collection: COLLECTION,
  single: '/person',
  fromDto: personFromDto,
  createToInput: personCreateToInput,
  updateId: (p) => p.personId,
  updateToInput: (p) => personUpdateToInput(p),
});

export const personApi = {
  ...base,

  /**
   * Lookup anti-duplicato: cerca un contatto del sito con questa email esatta.
   * Il backend risponde 200 col contatto se esiste, 204 No Content altrimenti
   * (axios -> data vuoto -> null).
   */
  getByEmailAddress: (groupId: number, emailAddress: string): Promise<Person | null> =>
    httpClient
      .get<PersonDto | ''>(`${COLLECTION}/${groupId}/by-email-address`, { params: { emailAddress } })
      .then((r) => (r.data ? personFromDto(r.data as PersonDto) : null)),
};
